import React, { useRef, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, Scissors, GripVertical, Trash2, Gauge, Wand2, Volume2, VolumeX } from 'lucide-react';
import { EditorSegment, AudioTrack, Caption, formatTime } from '../../types/editor';
import { isVideoFileSrc } from '../../lib/editorTimeline';
import AudioWaveform, { waveformSource } from './AudioWaveform';

const SPEEDS = [0.5, 1, 1.5, 2];

interface Props {
  segments: EditorSegment[];
  audioTracks: AudioTrack[];
  currentTime: number;
  totalDuration: number;
  activeSegmentId: string | null;
  activeAudioId: string | null;
  onSeek: (time: number) => void;
  onSegmentClick: (id: string) => void;
  onAudioClick: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDeleteSegment: (id: string) => void;
  onDeleteAudio: (id: string) => void;
  onUpdateAudio?: (id: string, updates: Partial<AudioTrack>) => void;
  onUpdateSegment?: (id: string, updates: Partial<EditorSegment>) => void;
  onSpeedChange: (id: string, speed: number) => void;
  onTrimChange: (id: string, trimStart: number, trimEnd: number) => void;
  onSplit: (id: string) => void;
  onEditWithKling: (id: string) => void;
  onRecolorWithKling?: (id: string) => void;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  captions?: Caption[];
}

const TRACK_HEIGHT = 52;
const AUDIO_TRACK_HEIGHT = 44;
const MARKER_INTERVAL = 5;

type ContextMenuState = {
  x: number;
  y: number;
  segmentId: string;
};

export default function EditorTimeline({
  segments, audioTracks, currentTime, totalDuration,
  activeSegmentId, activeAudioId,
  onSeek, onSegmentClick, onAudioClick, onReorder,
  onDeleteSegment, onDeleteAudio, onUpdateAudio, onUpdateSegment,
  onSpeedChange, onTrimChange, onSplit, onEditWithKling, onRecolorWithKling,
  zoom = 1, onZoomChange, captions = [],
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const [trimFocusId, setTrimFocusId] = useState<string | null>(null);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
    setSpeedMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!contextMenu) return;

    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    const onScroll = () => closeContextMenu();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [contextMenu, closeContextMenu]);

  const seekFromClientX = useCallback((clientX: number, trackEl: HTMLElement | null) => {
    if (!trackEl) return;
    const rect = trackEl.getBoundingClientRect();
    if (rect.width <= 0) return;
    const dur = Number.isFinite(totalDuration) && totalDuration > 0 ? totalDuration : 0;
    const t = ((clientX - rect.left) / rect.width) * dur;
    onSeek(Math.max(0, Math.min(dur, t)));
  }, [totalDuration, onSeek]);

  const handleTimelineClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    seekFromClientX(e.clientX, e.currentTarget);
  }, [seekFromClientX]);

  const handleClipSeekClick = (e: React.MouseEvent<HTMLElement>, segmentId: string) => {
    e.stopPropagation();
    onSegmentClick(segmentId);
    seekFromClientX(e.clientX, e.currentTarget.parentElement);
  };

  const safeTotal =
    Number.isFinite(totalDuration) && totalDuration > 0 ? totalDuration : 0;
  const playheadPercent =
    safeTotal > 0 && Number.isFinite(currentTime)
      ? Math.min(100, Math.max(0, (currentTime / safeTotal) * 100))
      : 0;

  // Time markers — cap count so Infinity/NaN duration never blows the array.
  const markerCount = Math.min(Math.floor(safeTotal / MARKER_INTERVAL), 500);
  const markers: number[] = [];
  for (let i = 0; i <= markerCount; i++) markers.push(i * MARKER_INTERVAL);
  if (safeTotal > 0 && markers[markers.length - 1] < safeTotal) markers.push(safeTotal);

  // Segment positions
  let cumulative = 0;
  const segmentPositions = segments.map(seg => {
    const speed = Number.isFinite(seg.speed) && seg.speed > 0 ? seg.speed : 1;
    const dur = Number.isFinite(seg.duration) && seg.duration > 0 ? seg.duration : 0;
    const offset = cumulative;
    const effectiveDur = dur / speed;
    cumulative += effectiveDur;
    return { offset, width: effectiveDur };
  });

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', index.toString());
    const seg = segments[index];
    if (seg) {
      e.dataTransfer.setData('application/x-seu-clip', JSON.stringify({
        id: seg.id,
        label: seg.label,
        type: seg.type,
        sourceUrl: seg.sourceUrl || '',
        blobUrl: seg.blobUrl || '',
        thumbnailUrl: seg.thumbnailUrl || '',
        generationContext: seg.generationContext || '',
      }));
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== toIndex) {
      onReorder(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleClipContextMenu = (e: React.MouseEvent, segmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSegmentClick(segmentId);
    setSpeedMenuOpen(false);
    setContextMenu({ x: e.clientX, y: e.clientY, segmentId });
  };

  const runMenuAction = (fn: () => void) => {
    fn();
    closeContextMenu();
  };

  const menuSegment = contextMenu
    ? segments.find(s => s.id === contextMenu.segmentId) || null
    : null;

  // Keep menu on-screen
  const menuStyle: React.CSSProperties = contextMenu ? (() => {
    const menuW = 220;
    const menuH = 220;
    const pad = 8;
    const x = Math.min(contextMenu.x, window.innerWidth - menuW - pad);
    const y = Math.min(contextMenu.y, window.innerHeight - menuH - pad);
    return {
      position: 'fixed' as const,
      left: Math.max(pad, x),
      top: Math.max(pad, y),
      zIndex: 9700,
      minWidth: menuW,
      background: 'rgba(18,18,24,0.98)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 8,
      boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
      padding: 4,
      fontFamily: 'inherit',
    };
  })() : {};

  const infoSegmentId = trimFocusId || activeSegmentId;

  return (
    <div style={{
      background: 'rgba(10,10,14,0.95)', borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
      padding: '8px 0',
      display: 'flex', flexDirection: 'column',
      minHeight: 0, height: '100%', maxHeight: '100%',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '0 12px 8px',
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Scissors size={12} /> Timeline
        </span>
        <div style={{ display: 'flex', gap: 8, fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(99,102,241,0.6)' }} /> Vídeo
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(168,85,247,0.6)' }} /> IA
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(34,197,94,0.6)' }} /> Clip
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(251,191,36,0.7)' }} /> Voz
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: 'rgba(34,211,238,0.7)' }} /> Som
          </span>
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
          {onZoomChange && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                onClick={() => onZoomChange(Math.max(1, +(zoom - 0.5).toFixed(1)))}
                style={zoomBtn}
              >−</button>
              <span>{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => onZoomChange(Math.min(4, +(zoom + 0.5).toFixed(1)))}
                style={zoomBtn}
              >+</button>
            </span>
          )}
          Total: {formatTime(safeTotal)}
        </span>
      </div>

      <div
        className="editor-timeline-lanes"
        style={{
          flex: 1,
          minHeight: 0,
          overflowX: zoom > 1 ? 'auto' : 'hidden',
          overflowY: 'auto',
        }}
      >
      <div style={{ minWidth: `${Math.max(100, zoom * 100)}%` }}>
      {/* Time markers */}
      <div
        onClick={(e) => seekFromClientX(e.clientX, containerRef.current || e.currentTarget)}
        style={{
        position: 'sticky', top: 0, zIndex: 6,
        height: 16, padding: '0 12px', margin: '4px 0',
        background: 'rgba(10,10,14,0.96)',
        cursor: 'pointer',
      }}>
        {markers.map(sec => (
          <span key={sec} style={{
            position: 'absolute',
            left: `calc(12px + ${(sec / (safeTotal || 1)) * 100}% * (100% - 24px) / 100%)`,
            transform: 'translateX(-50%)',
            fontSize: '0.55rem', color: 'rgba(255,255,255,0.25)', fontFamily: 'monospace',
            userSelect: 'none',
          }}>
            {formatTime(sec)}
          </span>
        ))}
      </div>

      {/* Video Track */}
      <div style={{ padding: '0 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600, width: 50 }}>🎬 Video</span>
        </div>
        <div
          ref={containerRef}
          onClick={handleTimelineClick}
          style={{
            position: 'relative', width: '100%', height: TRACK_HEIGHT,
            background: 'rgba(0,0,0,0.4)', borderRadius: 6, overflow: 'hidden',
            cursor: 'crosshair', border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          {segments.map((seg, i) => {
            const pos = segmentPositions[i];
            const left = safeTotal > 0 ? (pos.offset / safeTotal) * 100 : 0;
            const width = safeTotal > 0 ? (pos.width / safeTotal) * 100 : 0;
            const isActive = activeSegmentId === seg.id;
            const isAI = seg.type === 'ai_generated';
            const isDragging = dragIndex === i;
            const isDropTarget = dropIndex === i;

            return (
              <div
                key={seg.id}
                draggable
                onDragStart={(e) => handleDragStart(e, i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={(e) => handleDrop(e, i)}
                onDragEnd={handleDragEnd}
                onClick={(e) => handleClipSeekClick(e, seg.id)}
                onContextMenu={(e) => handleClipContextMenu(e, seg.id)}
                style={{
                  position: 'absolute',
                  left: `${left}%`, width: `${Math.max(width, 0.5)}%`,
                  height: '100%',
                  background: isAI
                    ? 'linear-gradient(135deg, rgba(168,85,247,0.3), rgba(139,92,246,0.5))'
                    : 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(79,70,229,0.4))',
                  borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.12)' : 'none',
                  borderRight: isDropTarget ? '2px solid #a855f7' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 4, cursor: 'grab',
                  opacity: isDragging ? 0.5 : 1,
                  outline: isActive ? '2px solid rgba(255,255,255,0.6)' : 'none',
                  outlineOffset: -2,
                  transition: 'outline 0.15s, opacity 0.15s',
                  overflow: 'hidden',
                }}
              >
                {/* Poster only — video URLs in <img> show the native broken-image icon. */}
                {seg.thumbnailUrl && !isVideoFileSrc(seg.thumbnailUrl) && (
                  <img
                    src={seg.thumbnailUrl}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    style={{
                    position: 'absolute', left: 0, top: 0,
                    width: '100%', height: '100%', objectFit: 'cover',
                    opacity: 0.3, pointerEvents: 'none',
                  }} />
                )}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 3, zIndex: 1 }}>
                  <GripVertical size={10} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                  {isAI && <Sparkles size={10} style={{ color: '#d8b4fe', flexShrink: 0 }} />}
                  <span style={{
                    fontSize: '0.55rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120,
                  }}>
                    {seg.label}
                  </span>
                  {seg.speed !== 1 && (
                    <span style={{ fontSize: '0.5rem', color: '#fbbf24', fontWeight: 600 }}>
                      {seg.speed}x
                    </span>
                  )}
                </div>
                {(seg.brightnessCurve || 'none') !== 'none' && (
                  <div
                    title="Curva de brilho"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 3,
                      background: 'linear-gradient(90deg, rgba(251,191,36,0.85), transparent 22%, transparent 78%, rgba(251,191,36,0.85))',
                      pointerEvents: 'none',
                    }}
                  />
                )}
                {i < segments.length - 1 && (seg.transitionOut === 'fade' || seg.transitionOut === 'dissolve') && (
                  <div
                    title={seg.transitionOut === 'dissolve' ? 'Dissolve' : 'Fade'}
                    style={{
                      position: 'absolute', right: -5, top: '50%', transform: 'translateY(-50%) rotate(45deg)',
                      width: 8, height: 8,
                      background: seg.transitionOut === 'dissolve' ? '#818cf8' : '#fbbf24',
                      zIndex: 5, pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            );
          })}

          {/* Playhead */}
          <div style={{
            position: 'absolute', left: `${playheadPercent}%`, top: 0,
            width: 2, height: '100%',
            background: '#ef4444', boxShadow: '0 0 6px rgba(239,68,68,0.5)',
            zIndex: 20, pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            left: `calc(${playheadPercent}% - 5px)`, top: 0,
            width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid #ef4444',
            zIndex: 21, pointerEvents: 'none',
          }} />
        </div>
      </div>

      {/* Audio embedded in video clips (always visible — not only imported MP3) */}
      {segments.some((s) => s.type !== 'image') && (
        <div style={{ padding: '6px 12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600, width: 50 }}>🎵 Áudio</span>
          </div>
          <div
            onClick={handleTimelineClick}
            style={{
              position: 'relative', width: '100%', height: AUDIO_TRACK_HEIGHT,
              background: 'rgba(0,0,0,0.3)', borderRadius: 4, overflow: 'hidden',
              cursor: 'crosshair', border: '1px solid rgba(255,255,255,0.04)',
            }}
          >
            {segments.map((seg, i) => {
              if (seg.type === 'image') return null;
              const pos = segmentPositions[i];
              const left = safeTotal > 0 ? (pos.offset / safeTotal) * 100 : 0;
              const width = safeTotal > 0 ? (pos.width / safeTotal) * 100 : 0;
              const isActive = activeSegmentId === seg.id;
              return (
                <div
                  key={`audio-${seg.id}`}
                  onClick={(e) => handleClipSeekClick(e, seg.id)}
                  onContextMenu={(e) => handleClipContextMenu(e, seg.id)}
                  title={seg.muted ? 'Áudio do clipe (mudo)' : 'Áudio do clipe'}
                  style={{
                    position: 'absolute',
                    left: `${left}%`, width: `${Math.max(width, 0.5)}%`,
                    height: '100%',
                    background: seg.muted ? 'rgba(255,255,255,0.05)' : 'rgba(22,101,52,0.28)',
                    outline: isActive ? '2px solid rgba(34,197,94,0.65)' : 'none',
                    outlineOffset: -2,
                    opacity: seg.muted ? 0.5 : 1,
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                >
                  <AudioWaveform
                    source={waveformSource(seg.blob, seg.blobUrl, seg.sourceUrl)}
                    trimStart={seg.trimStart}
                    trimEnd={seg.trimEnd}
                    originalDuration={seg.originalDuration}
                    muted={seg.muted}
                  />
                  <div style={{
                    position: 'relative', zIndex: 1, height: '100%',
                    display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px',
                  }}>
                  {onUpdateSegment && (
                    <button
                      type="button"
                      title={seg.muted ? 'Ativar áudio do clipe' : 'Mutar áudio do clipe'}
                      onClick={(e) => { e.stopPropagation(); onUpdateSegment(seg.id, { muted: !seg.muted }); }}
                      style={{
                        background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 4, padding: '2px 5px', color: seg.muted ? '#f87171' : '#86efac',
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                      }}
                    >
                      {seg.muted ? <VolumeX size={9} /> : <Volume2 size={9} />}
                    </button>
                  )}
                  {!onUpdateSegment && seg.muted
                    ? <VolumeX size={10} style={{ color: 'rgba(255,255,255,0.45)', flexShrink: 0 }} />
                    : null}
                  {isActive && (
                    <button
                      type="button"
                      title="Dividir no playhead"
                      onClick={(e) => { e.stopPropagation(); onSplit(seg.id); }}
                      style={{
                        marginLeft: 'auto', flexShrink: 0,
                        background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(34,197,94,0.4)',
                        borderRadius: 4, padding: '2px 5px', color: '#86efac', cursor: 'pointer',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <Scissors size={9} />
                    </button>
                  )}
                  </div>
                </div>
              );
            })}
            <div style={{
              position: 'absolute', left: `${playheadPercent}%`, top: 0,
              width: 1, height: '100%',
              background: '#ef4444', opacity: 0.7,
              zIndex: 10, pointerEvents: 'none',
            }} />
          </div>
        </div>
      )}

      <OverlayAudioLane
        label="🎤 Voz"
        emptyHint="Voz e locução da IA nesta faixa"
        accent="voice"
        tracks={audioTracks.filter(isVoiceTrack)}
        safeTotal={safeTotal}
        playheadPercent={playheadPercent}
        activeAudioId={activeAudioId}
        onTimelineClick={handleTimelineClick}
        onAudioClick={onAudioClick}
        onDeleteAudio={onDeleteAudio}
        onUpdateAudio={onUpdateAudio}
      />
      <OverlayAudioLane
        label="🔊 Som"
        emptyHint="Ambiente, efeitos e trilhas importadas"
        accent="sfx"
        tracks={audioTracks.filter((t) => !isVoiceTrack(t))}
        safeTotal={safeTotal}
        playheadPercent={playheadPercent}
        activeAudioId={activeAudioId}
        onTimelineClick={handleTimelineClick}
        onAudioClick={onAudioClick}
        onDeleteAudio={onDeleteAudio}
        onUpdateAudio={onUpdateAudio}
      />
      {captions.length > 0 && (
        <div style={{ padding: '6px 12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', fontWeight: 600, width: 50 }}>Aa Texto</span>
          </div>
          <div style={{
            position: 'relative', width: '100%', height: 22,
            background: 'rgba(0,0,0,0.3)', borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden',
          }}>
            {captions.map((cap) => {
              const left = safeTotal > 0 ? (cap.startTime / safeTotal) * 100 : 0;
              const width = safeTotal > 0 ? (Math.max(0.1, cap.endTime - cap.startTime) / safeTotal) * 100 : 0;
              return (
                <div
                  key={cap.id}
                  title={cap.text}
                  style={{
                    position: 'absolute',
                    left: `${left}%`, width: `${Math.max(width, 0.5)}%`,
                    height: '100%',
                    background: cap.isImpactPhrase ? 'rgba(251,191,36,0.28)' : 'rgba(255,255,255,0.12)',
                    display: 'flex', alignItems: 'center', padding: '0 4px',
                    overflow: 'hidden',
                  }}
                >
                  <span style={{
                    fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {cap.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
      </div>

      {activeAudioId && onUpdateAudio && (() => {
        const track = audioTracks.find((t) => t.id === activeAudioId);
        if (!track) return null;
        const kindLabel = isVoiceTrack(track) ? 'Voz' : track.kind === 'import' ? 'Importado' : 'Som';
        return (
          <div style={{
            margin: '6px 12px 4px', padding: '6px 10px',
            background: 'rgba(255,255,255,0.03)', borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)',
          }}>
            <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{track.name}</span>
            <span>{kindLabel}</span>
            <button
              type="button"
              title={track.muted ? 'Ativar trilha' : 'Mutar trilha'}
              onClick={() => onUpdateAudio(track.id, { muted: !track.muted })}
              style={{
                background: track.muted ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${track.muted ? 'rgba(239,68,68,0.35)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 4, padding: '2px 6px',
                color: track.muted ? '#f87171' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                fontSize: '0.58rem', fontFamily: 'inherit',
              }}
            >
              {track.muted ? <VolumeX size={10} /> : <Volume2 size={10} />}
              {track.muted ? 'Mudo' : 'Ativo'}
            </button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Vol
              <input
                type="range" min={0} max={1} step={0.05}
                value={track.muted ? 0 : track.volume}
                onChange={(e) => onUpdateAudio(track.id, { volume: parseFloat(e.target.value), muted: false })}
                style={{ width: 80, accentColor: '#22d3ee', height: 3 }}
              />
              <span>{track.muted ? 'off' : `${Math.round(track.volume * 100)}%`}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Início
              <input
                type="number" min={0} step={0.1}
                value={track.startOffset}
                onChange={(e) => onUpdateAudio(track.id, { startOffset: Math.max(0, parseFloat(e.target.value) || 0) })}
                style={{
                  width: 52, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 4, padding: '2px 4px', color: '#fff', fontSize: '0.58rem', fontFamily: 'monospace',
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => onUpdateAudio(track.id, { loop: !track.loop })}
              style={{
                background: track.loop ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${track.loop ? 'rgba(34,197,94,0.35)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 4, padding: '2px 6px',
                color: track.loop ? '#4ade80' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer', fontSize: '0.58rem', fontFamily: 'inherit',
              }}
            >
              {track.loop ? 'Loop on' : 'Loop'}
            </button>
            <button
              type="button"
              onClick={() => onDeleteAudio(track.id)}
              style={{
                marginLeft: 'auto', background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4,
                padding: '2px 6px', color: '#f87171', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 3,
                fontSize: '0.58rem', fontFamily: 'inherit',
              }}
            >
              <Trash2 size={9} /> Remover
            </button>
          </div>
        );
      })()}

      {/* Active segment info bar */}
      {infoSegmentId && (() => {
        const seg = segments.find(s => s.id === infoSegmentId);
        if (!seg) return null;
        const showTrim = trimFocusId === seg.id;
        return (
          <div style={{
            margin: '6px 12px 4px', padding: '6px 10px',
            background: 'rgba(255,255,255,0.03)', borderRadius: 6,
            border: showTrim ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(255,255,255,0.06)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)',
          }}>
            <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{seg.label}</span>
            <span>Duração: {formatTime(seg.duration)}</span>
            <span>Velocidade: {seg.speed}x</span>

            {showTrim && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <Scissors size={10} style={{ color: '#818cf8' }} />
                <label style={{ color: 'rgba(255,255,255,0.45)' }}>Início:</label>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, seg.originalDuration * 0.9)}
                  step={0.1}
                  value={seg.trimStart}
                  onChange={(e) => onTrimChange(seg.id, parseFloat(e.target.value), seg.trimEnd)}
                  style={{ width: 70, accentColor: '#6366f1', height: 3 }}
                />
                <label style={{ color: 'rgba(255,255,255,0.45)' }}>Fim:</label>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, seg.originalDuration * 0.9)}
                  step={0.1}
                  value={seg.trimEnd}
                  onChange={(e) => onTrimChange(seg.id, seg.trimStart, parseFloat(e.target.value))}
                  style={{ width: 70, accentColor: '#6366f1', height: 3 }}
                />
                <button
                  onClick={() => onSplit(seg.id)}
                  style={{
                    background: 'rgba(99,102,241,0.12)',
                    border: '1px solid rgba(99,102,241,0.3)', borderRadius: 4,
                    padding: '2px 6px', color: '#a5b4fc', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 3,
                    fontSize: '0.58rem', fontFamily: 'inherit',
                  }}
                  title="Dividir no playhead"
                >
                  <Scissors size={9} /> Dividir no playhead
                </button>
                <button
                  onClick={() => setTrimFocusId(null)}
                  style={{
                    background: 'transparent', border: 'none',
                    color: 'rgba(255,255,255,0.35)', cursor: 'pointer',
                    fontSize: '0.55rem', fontFamily: 'inherit', padding: 2,
                  }}
                >
                  Fechar
                </button>
              </div>
            )}

            <button
              onClick={() => onDeleteSegment(seg.id)}
              style={{
                marginLeft: 'auto', background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.25)', borderRadius: 4,
                padding: '2px 6px', color: '#f87171', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 3,
                fontSize: '0.58rem', fontFamily: 'inherit',
              }}
            >
              <Trash2 size={9} /> Remover
            </button>
          </div>
        );
      })()}

      {/* Context menu */}
      {contextMenu && menuSegment && createPortal(
        <div ref={menuRef} style={menuStyle} role="menu">
          <ContextMenuItem
            icon={<Trash2 size={12} />}
            label="Remover a parte"
            danger
            onClick={() => runMenuAction(() => onDeleteSegment(menuSegment.id))}
          />
          <div style={{ position: 'relative' }}>
            <ContextMenuItem
              icon={<Gauge size={12} />}
              label="Editar velocidade"
              onClick={() => setSpeedMenuOpen(o => !o)}
              trailing={menuSegment.speed !== 1 ? `${menuSegment.speed}x` : undefined}
            />
            {speedMenuOpen && (
              <div style={{
                display: 'flex', gap: 3, padding: '4px 8px 8px',
              }}>
                {SPEEDS.map(s => (
                  <button
                    key={s}
                    onClick={() => runMenuAction(() => onSpeedChange(menuSegment.id, s))}
                    style={{
                      flex: 1, padding: '4px 0', borderRadius: 4, border: 'none',
                      fontSize: '0.62rem', fontFamily: 'inherit', fontWeight: menuSegment.speed === s ? 600 : 400,
                      background: menuSegment.speed === s ? 'var(--primary, #6366f1)' : 'rgba(255,255,255,0.06)',
                      color: menuSegment.speed === s ? '#fff' : 'rgba(255,255,255,0.55)',
                      cursor: 'pointer',
                    }}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            )}
          </div>
          <ContextMenuItem
            icon={<Scissors size={12} />}
            label="Dividir no playhead"
            onClick={() => runMenuAction(() => onSplit(menuSegment.id))}
          />
          <ContextMenuItem
            icon={<Scissors size={12} />}
            label="Cortar (início/fim)"
            onClick={() => runMenuAction(() => {
              onSegmentClick(menuSegment.id);
              setTrimFocusId(menuSegment.id);
            })}
          />
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '3px 4px' }} />
          <ContextMenuItem
            icon={<Wand2 size={12} />}
            label="Editar com IA V2V no Kling"
            accent
            onClick={() => runMenuAction(() => onEditWithKling(menuSegment.id))}
          />
          {onRecolorWithKling && (
            <ContextMenuItem
              icon={<Wand2 size={12} />}
              label="Recolor objetos (chroma)"
              accent
              onClick={() => runMenuAction(() => onRecolorWithKling(menuSegment.id))}
            />
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function isVoiceTrack(track: AudioTrack) {
  return (track.kind || 'voice') === 'voice';
}

function OverlayAudioLane({
  label,
  emptyHint,
  accent,
  tracks,
  safeTotal,
  playheadPercent,
  activeAudioId,
  onTimelineClick,
  onAudioClick,
  onDeleteAudio,
  onUpdateAudio,
}: {
  label: string;
  emptyHint: string;
  accent: 'voice' | 'sfx';
  tracks: AudioTrack[];
  safeTotal: number;
  playheadPercent: number;
  activeAudioId: string | null;
  onTimelineClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onAudioClick: (id: string) => void;
  onDeleteAudio: (id: string) => void;
  onUpdateAudio?: (id: string, updates: Partial<AudioTrack>) => void;
}) {
  const isVoice = accent === 'voice';
  const accentColor = isVoice ? 'rgba(251,191,36,0.75)' : 'rgba(34,211,238,0.8)';
  const border = isVoice ? 'rgba(251,191,36,0.18)' : 'rgba(34,211,238,0.2)';
  const fill = isVoice ? 'rgba(202,138,4,0.38)' : 'rgba(8,145,178,0.4)';
  const emptyBorder = isVoice ? 'rgba(251,191,36,0.28)' : 'rgba(34,211,238,0.28)';
  const emptyBg = isVoice ? 'rgba(120,53,15,0.12)' : 'rgba(8,47,73,0.18)';
  const emptyColor = isVoice ? 'rgba(253,224,71,0.45)' : 'rgba(165,243,252,0.45)';
  const wave = isVoice ? 'rgba(253,224,71,0.92)' : 'rgba(103,232,249,0.92)';

  return (
    <div style={{ padding: '6px 12px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
        <span style={{ fontSize: '0.55rem', color: accentColor, fontWeight: 700, width: 58 }}>{label}</span>
      </div>
      {tracks.length === 0 && (
        <div style={{
          height: AUDIO_TRACK_HEIGHT,
          borderRadius: 4,
          border: `1px dashed ${emptyBorder}`,
          background: emptyBg,
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          fontSize: '0.55rem',
          color: emptyColor,
        }}>
          {emptyHint}
        </div>
      )}
      {tracks.map((track) => {
        const left = safeTotal > 0 ? (track.startOffset / safeTotal) * 100 : 0;
        const effectiveDur = (track.duration - track.trimStart - track.trimEnd);
        const width = safeTotal > 0 ? (effectiveDur / safeTotal) * 100 : 0;
        const isActive = activeAudioId === track.id;
        const muted = Boolean(track.muted);

        return (
          <div
            key={track.id}
            style={{
              position: 'relative', width: '100%', height: AUDIO_TRACK_HEIGHT,
              background: 'rgba(0,0,0,0.3)', borderRadius: 4, overflow: 'hidden',
              cursor: 'pointer', border: `1px solid ${border}`,
              marginBottom: 3, opacity: muted ? 0.55 : 1,
            }}
            onClick={(e) => {
              onAudioClick(track.id);
              onTimelineClick(e);
            }}
          >
            <div style={{
              position: 'absolute',
              left: `${left}%`, width: `${Math.max(width, 0.5)}%`,
              height: '100%',
              background: muted ? 'rgba(255,255,255,0.06)' : fill,
              outline: isActive ? `2px solid ${accentColor}` : 'none',
              outlineOffset: -2,
              overflow: 'hidden',
            }}>
              <AudioWaveform
                source={waveformSource(track.blob, track.blobUrl)}
                trimStart={track.trimStart}
                trimEnd={track.trimEnd}
                originalDuration={track.duration}
                muted={muted}
                color={wave}
              />
              <div style={{
                position: 'relative', zIndex: 1, height: '100%',
                display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px',
              }}>
                {onUpdateAudio && (
                  <button
                    type="button"
                    title={muted ? 'Ativar trilha' : 'Mutar trilha'}
                    onClick={(e) => { e.stopPropagation(); onUpdateAudio(track.id, { muted: !track.muted }); }}
                    style={{
                      background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 4, padding: '2px 4px',
                      color: muted ? '#f87171' : accentColor,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0,
                    }}
                  >
                    {muted ? <VolumeX size={9} /> : <Volume2 size={9} />}
                  </button>
                )}
                <span style={{
                  fontSize: '0.55rem', color: 'rgba(255,255,255,0.55)', fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                }}>
                  {track.name}
                </span>
                {isActive && onUpdateAudio && (
                  <input
                    type="range" min={0} max={1} step={0.05}
                    value={muted ? 0 : track.volume}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      e.stopPropagation();
                      onUpdateAudio(track.id, { volume: parseFloat(e.target.value), muted: false });
                    }}
                    style={{ width: 56, accentColor: isVoice ? '#fbbf24' : '#22d3ee', height: 3 }}
                  />
                )}
                <span style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>
                  {muted ? 'mudo' : `${Math.round(track.volume * 100)}%`}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteAudio(track.id); }}
                  style={{
                    background: 'none', border: 'none', color: '#f87171',
                    cursor: 'pointer', padding: 2, display: 'flex',
                  }}
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>

            <div style={{
              position: 'absolute', left: `${playheadPercent}%`, top: 0,
              width: 1, height: '100%',
              background: '#ef4444', opacity: 0.6,
              zIndex: 10, pointerEvents: 'none',
            }} />
          </div>
        );
      })}
    </div>
  );
}

function ContextMenuItem({
  icon, label, onClick, danger, accent, trailing,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  accent?: boolean;
  trailing?: string;
}) {
  const [hover, setHover] = useState(false);
  const color = danger ? '#f87171' : accent ? '#d8b4fe' : 'rgba(255,255,255,0.75)';
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px', border: 'none', borderRadius: 5,
        background: hover
          ? (danger ? 'rgba(239,68,68,0.12)' : accent ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.06)')
          : 'transparent',
        color, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: '0.68rem', fontWeight: 500, textAlign: 'left',
      }}
    >
      <span style={{ display: 'flex', opacity: 0.85 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {trailing && (
        <span style={{ fontSize: '0.58rem', color: '#fbbf24', fontWeight: 600 }}>{trailing}</span>
      )}
    </button>
  );
}

const zoomBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  color: 'rgba(255,255,255,0.55)',
  width: 16,
  height: 16,
  fontSize: '0.65rem',
  lineHeight: '14px',
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
